import { useModalStore } from "@/hooks/Modal/hooks";
import { Button } from "antd";
import { Observer, observer } from "mobx-react-lite";
import React from "react";
import MultiPlatformEwaybillResultStore, { EwaybillTableColumnData } from "../MultiPlatformEwaybillResultStore";
import RemarkPrintFailOrderModal from "@/scenes/OrderManagement/components/dialogs/PrintEwaybillResultDialog/PrintFailOperation/RemarkPrintFailOrderModal";
import useWaybillResultTableColumns from "../useWaybillResultTableColumns";
import GlobalStore from "@/store";
import type { TableColumnsType } from "antd";
import { getAutoRowSpan } from "@/utils/table";
import OrderMergeSplitStatus from "@/enum/OrderMergeSplitStatus";
import * as OrderUtils from "@/utils/OrderUtils";
import classNames from "classnames";
import { CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import LoadingIcon from "@/components/LoadingIcon";
import { useGlobalStore } from "@/store/hooks";
import OrderSellerRemarksDTO from "@/dto/order/OrderSellerRemarksDTO";
import openBatchUpdateSellerRemarksModal from "../../../BottomOperationBar/OtherOperationsButton/components/services/batchUpdateSellerRemarks";
import { BatchUpdateSellerRemarksDialogProps } from "@/scenes/OrderManagement/components/dialogs/BatchUpdateSellerRemarksDialog";

/**
 * @see{@link useWaybillResultTableColumns} 类似逻辑
 */
function buildColumn(store: MultiPlatformEwaybillResultStore, globalStore: typeof GlobalStore) {
    const columns: TableColumnsType<EwaybillTableColumnData> = [
        {
            title: "店铺信息",
            width: 100,
            key: "shopId",
            render(value, record, index) {
                const shop = globalStore.shopMapping.get(record.shopId);

                return <span>{shop?.name}</span>;
            },
        },
        {
            title: "收货人信息",
            key: "consignee",
            render(_, value) {
                return (
                    <Observer>
                        {() => {
                            const order = store.orderMapping.get(value.orderId);

                            if (!order) {
                                return null;
                            }

                            const orderOriginalIds: string[] = [];
                            // 如果是合并订单
                            if (order.mergeSplitStatus === OrderMergeSplitStatus.MERGE_PARENT) {
                                order.childOrders?.forEach((childOrder) => {
                                    orderOriginalIds.push(childOrder.originalId);
                                });
                            } else {
                                orderOriginalIds.push(order.originalId);
                            }

                            return (
                                <div>
                                    <p
                                        style={{ wordBreak: "break-all", fontSize: 11, lineHeight: 1 }}
                                        className="text-gray"
                                    >
                                        订单编号:{orderOriginalIds.join(",")}
                                    </p>

                                    <span>{OrderUtils.getConsigneeName(order)}，</span>
                                    <span>{OrderUtils.getConsigneeAddress(order)} </span>
                                    <span>{OrderUtils.getConsigneeMobile(order)} </span>
                                    <span>{OrderUtils.getConsigneeTelephone(order)} </span>
                                </div>
                            );
                        }}
                    </Observer>
                );
            },
            onCell(value, index) {
                return {
                    rowSpan: getAutoRowSpan(store.dataSource, value, index, "orderId"),
                };
            },
        },
        {
            width: 230,
            title: "电子面单号获取结果",
            key: "ewaybills",
            render(_, value) {
                return (
                    <Observer>
                        {() => {
                            const requestGenerateError = store.requestGenerateErrorResultMapping.get(value.orderId);

                            if (requestGenerateError) {
                                return <span className="text-error">{requestGenerateError}</span>;
                            }

                            if (!value.ewaybill) {
                                if (value.errorMsg) {
                                    return <span className="text-error">{value.errorMsg}</span>;
                                }
                                return null;
                            }

                            const printError = store.waybillNoPrintErrorsMapping.get(value.ewaybill.waybillNo);

                            const printIndex = store.printIndexMapping.get(value.ewaybill?.waybillNo);

                            return (
                                <div>
                                    <span>
                                        {value.ewaybill.waybillNo}

                                        {printIndex ? (
                                            <span
                                                className={classNames({
                                                    "text-error": !!printError,
                                                })}
                                                style={{
                                                    fontSize: 12,
                                                    color: "rgba(0,0,0,0.412)",
                                                    userSelect: "none",
                                                }}
                                            >
                                                <span>{"　"}</span>
                                                {printIndex}
                                                <span>{"　"}</span>
                                            </span>
                                        ) : null}
                                    </span>

                                    {value.ewaybill.parentWaybillNo ? (
                                        <p>
                                            <span className="small text-muted">
                                                母号：{value.ewaybill.parentWaybillNo}
                                            </span>
                                        </p>
                                    ) : null}
                                </div>
                            );
                        }}
                    </Observer>
                );
            },
        },
    ];

    if (!store.isGenerateWaybillNoOnly) {
        columns.push({
            width: 150,
            title: "发送到打印机结果",
            key: "printResult",
            render(_, value) {
                return (
                    <Observer>
                        {() => {
                            const requestGenerateError = store.requestGenerateErrorResultMapping.get(value.orderId);

                            if (requestGenerateError) {
                                return null;
                            }

                            if (value.errorMsg) {
                                return null;
                            }

                            const success = store.waybillNoPrintSuccessMapping.get(value.ewaybill?.waybillNo);

                            if (success) {
                                return <CheckCircleFilled className="success" />;
                            }

                            const error = store.waybillNoPrintErrorsMapping.get(value.ewaybill?.waybillNo);

                            if (error) {
                                const hasTutorial = error?.includes("当前无法发送指令");

                                return (
                                    <span className="text-error">
                                        <CloseCircleFilled />
                                        原因：{error}
                                        {hasTutorial ? (
                                            <a
                                                className="ml5"
                                                target="_blank"
                                                href="https://qw15p3s8hv.feishu.cn/wiki/wikcnP2uK23RrtTFGyYWuUn3ILy"
                                            >
                                                查看解决办法
                                            </a>
                                        ) : null}
                                    </span>
                                );
                            }

                            return <LoadingIcon time={0.5} />;
                        }}
                    </Observer>
                );
            },
        });
    }

    return columns;
}

interface RemarkPrintFailOrderButtonProps
    extends Pick<BatchUpdateSellerRemarksDialogProps, "onUpdateOrderSellerRemarks" | "onUpdateSupplierOrderRemarks"> {
    store: MultiPlatformEwaybillResultStore;
}

const RemarkPrintFailOrderButton: React.FC<RemarkPrintFailOrderButtonProps> = function (props) {
    const { store, onUpdateOrderSellerRemarks, onUpdateSupplierOrderRemarks } = props;
    const modalStore = useModalStore();
    const globalStore = useGlobalStore();

    return (
        <Button
            className="mr10"
            onClick={() => {
                const columns = buildColumn(store, globalStore);

                modalStore.openModal(RemarkPrintFailOrderModal, {
                    store,
                    onBatchRemarkButtonClick(selectedOrders) {
                        openBatchUpdateSellerRemarksModal({
                            orders: selectedOrders,
                            onUpdateOrderSellerRemarks,
                            onUpdateSupplierOrderRemarks,
                            modalStore,
                            globalStore,
                        });
                    },
                    columns,
                });
            }}
        >
            备注打印失败订单
        </Button>
    );
};

export default observer(RemarkPrintFailOrderButton);
